import React from 'react';
import { Container, Row, Col, Card, ListGroup } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const SiteMap: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Site Map | Ashridge Park FC</title>
        <meta name="description" content="Complete site map of Ashridge Park Football Club website. Find all pages and sections easily." />
      </Helmet>
      
      <Container className="p-3 my-4">
        <h1 className="mb-4">SITE MAP</h1>
        <p className="mb-4">Find all pages and sections of the Ashridge Park FC website:</p>
        
        <Row>
          {/* Main Pages */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header as="h5" style={{ backgroundColor: '#254E97', color: 'white' }}>
                Main Pages
              </Card.Header>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <Link to="/" className="text-decoration-none">
                    Home
                  </Link>
                  <small className="text-muted d-block">Welcome page with club overview and contact form</small>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Link to="/teams" className="text-decoration-none">
                    Our Teams
                  </Link>
                  <small className="text-muted d-block">Complete list of boys and girls teams with manager details</small>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Link to="/matchdayinfo" className="text-decoration-none">
                    Match Day Info
                  </Link>
                  <small className="text-muted d-block">Ground information, maps, and fixture templates</small>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Link to="/clubinfo" className="text-decoration-none">
                    Club Info
                  </Link>
                  <small className="text-muted d-block">Club history, policies, and general information</small>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Link to="/shop" className="text-decoration-none">
                    Shop
                  </Link>
                  <small className="text-muted d-block">Official merchandise and kit from our partner store</small>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Link to="/soccerschool" className="text-decoration-none">
                    Soccer School
                  </Link>
                  <small className="text-muted d-block">Information about our Soccer School for young players</small>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Link to="/schedule" className="text-decoration-none">
                    Fixture Schedule
                  </Link>
                  <small className="text-muted d-block">Fixture list: home games with refs/linos/pitch etc</small>
                </ListGroup.Item>
              </ListGroup>
            </Card>
          </Col>

          {/* Additional Pages */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header as="h5" style={{ backgroundColor: '#254E97', color: 'white' }}>
                Additional Pages
              </Card.Header>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <Link to="/playerswanted" className="text-decoration-none">
                    Players Wanted
                  </Link>
                  <small className="text-muted d-block">Current player recruitment opportunities</small>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Link to="/tournament" className="text-decoration-none">
                    Tournament
                  </Link>
                  <small className="text-muted d-block">Tournament information and events</small>
                </ListGroup.Item>
                {/* <ListGroup.Item>
                  <Link to="/tournament-registration" className="text-decoration-none">
                    Tournament Registration
                  </Link>
                  <small className="text-muted d-block">Register your team for the Summer Tournament 2026</small>
                </ListGroup.Item> */}
                <ListGroup.Item>
                  <Link to="/coachescorner" className="text-decoration-none">
                    Coaches Corner
                  </Link>
                  <small className="text-muted d-block">Resources and downloads for club coaches</small>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Link to="/privacy-policy" className="text-decoration-none">
                    Privacy Policy
                  </Link>
                  <small className="text-muted d-block">Cookie usage and privacy information</small>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Link to="/sitemap" className="text-decoration-none">
                    Site Map
                  </Link>
                  <small className="text-muted d-block">This page - complete site navigation</small>
                </ListGroup.Item>
              </ListGroup>
            </Card>
          </Col>
        </Row>

        <Row>
          {/* Key Features */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header as="h5" style={{ backgroundColor: '#254E97', color: 'white' }}>
                Key Features
              </Card.Header>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <strong>Contact Form</strong>
                  <small className="text-muted d-block">Available on home page and teams page modal</small>
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Team Manager Details</strong>
                  <small className="text-muted d-block">Direct contact information for all teams</small>
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Interactive Maps</strong>
                  <small className="text-muted d-block">Google Maps showing ground locations</small>
                </ListGroup.Item>
                <ListGroup.Item>
                  <strong>Downloadable Resources</strong>
                  <small className="text-muted d-block">Fixture templates and match day information</small>
                </ListGroup.Item>
              </ListGroup>
            </Card>
          </Col>

          {/* Quick Actions */}
          <Col md={6} className="mb-4">
            <Card>
              <Card.Header as="h5" style={{ backgroundColor: '#254E97', color: 'white' }}>
                Quick Actions
              </Card.Header>
              <ListGroup variant="flush">
                <ListGroup.Item>
                  <a href="/content/2025-26-Fixture-Notification-Template.docx" download className="text-decoration-none">
                    Download Fixture Template
                  </a>
                  <small className="text-muted d-block">Word document for match notifications</small>
                </ListGroup.Item>
                <ListGroup.Item>
                  <a href="http://www.sportsscenedirect.com/ashridge-park-108-c.asp" target="_blank" rel="noopener noreferrer" className="text-decoration-none">
                    Visit Online Shop
                  </a>
                  <small className="text-muted d-block">Official club merchandise store</small>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Link to="/teams" className="text-decoration-none">
                    Find Your Team
                  </Link>
                  <small className="text-muted d-block">Browse all age groups and contact managers</small>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Link to="/" className="text-decoration-none">
                    Get in Touch
                  </Link>
                  <small className="text-muted d-block">Use our contact form for general enquiries</small>
                </ListGroup.Item>
                <ListGroup.Item>
                  <Link to="/registrationdoc" download className="text-decoration-none">
                    EBFA Registration Details
                  </Link>
                  <small className="text-muted d-block">EBFA Regisration document</small>
                </ListGroup.Item>
              </ListGroup>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default SiteMap;